import { DocumentData } from 'features/baseLens/types/documentTypes'
import { DocumentType } from 'features/files'
import { File, FileText, Image } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

import { DocumentActions } from './DocumentActions'

interface DocumentCardProps {
  document: DocumentData
  onView?: () => void
  onDownload?: () => void
  onDelete?: () => void
  showActions?: boolean
  canView?: boolean
  canDownload?: boolean
  canDelete?: boolean
  className?: string
}

const StyledDocumentActions = styled(DocumentActions)`
  opacity: 0;
`

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: ${({ theme }) => theme.forecast.background2};
  border: 1px solid ${({ theme }) => theme.forecast.background3};
  border-radius: 0.5rem;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;

  &:hover {
    background: ${({ theme }) => theme.forecast.background3};
    border-color: ${({ theme }) => theme.forecast.primary};
    transform: translateY(-1px);
    box-shadow: 0 0.25rem 0.75rem rgba(0, 0, 0, 0.1);

    ${StyledDocumentActions} {
      opacity: 1;
    }
  }
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  background: ${({ theme }) => theme.forecast.background1};
  border: 1px solid ${({ theme }) => theme.forecast.background3};
  border-radius: 0.5rem;
  flex-shrink: 0;
`

const Info = styled.div`
  flex: 1;
  min-width: 0;
`

const Title = styled.h3`
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
`

const Thumbnail = styled.img`
  width: 3rem;
  height: 3rem;
  object-fit: cover;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.forecast.background3};
`

const getFileIcon = (document: DocumentData): React.JSX.Element => {
  const type = document.type

  let fileComponent = null

  switch (type) {
    case DocumentType.PDF:
      fileComponent = <FileText size={24} color='#ff4d4f' />
      break
    case DocumentType.EXCEL:
      fileComponent = <FileText size={24} color='#52c41a' />
      break
    case DocumentType.WORD:
      fileComponent = <FileText size={24} color='#1677ff' />
      break
    case DocumentType.IMAGE:
      if (document.signedUrl) {
        return <Thumbnail src={document.signedUrl} alt={document.title} />
      }
      fileComponent = <Image size={24} color='#722ed1' />
      break
    default:
      fileComponent = <File size={24} color='#8c8c8c' />
  }

  return <IconContainer>{fileComponent}</IconContainer>
}

const formatDate = (date: string): string => {
  try {
    return new Date(date).toLocaleDateString()
  } catch {
    return 'Unknown'
  }
}

export const DocumentCard: React.FC<DocumentCardProps> = ({
  document,
  onView,
  onDownload,
  onDelete,
  showActions = true,
  canView = true,
  canDownload = true,
  canDelete = true,
  className,
}) => {
  return (
    <Card className={className}>
      {getFileIcon(document)}

      <Info>
        <Title>{document.title}</Title>
        <Meta>
          <span>{document.type.toUpperCase()}</span>
          {document.size && (
            <>
              <span>•</span>
              <span>{document.size}</span>
            </>
          )}
          {document.uploadDate && (
            <>
              <span>•</span>
              <span>{formatDate(document.uploadDate)}</span>
            </>
          )}
        </Meta>
      </Info>

      {showActions && (
        <StyledDocumentActions
          onView={onView}
          onDownload={onDownload}
          onDelete={onDelete}
          deleteConfirmDescription={`Are you sure you want to delete "${document.title}"?`}
          canView={canView}
          canDownload={canDownload}
          canDelete={canDelete}
        />
      )}
    </Card>
  )
}
